import { computed, ref, watch } from 'vue';
import SalesTransactionsAPI from 'dashboard/api/salesTransactions';
import { salesReceiptReviewContext } from './salesReceiptReviewContext';
import { salesReviewLocation } from './salesReviewNavigation';
import { saveReviewNavigationBeforeSwitch } from './salesReviewNavigationPersistence';

const errorMessage = (error, fallback) =>
  error?.response?.data?.message || error?.response?.data?.error || fallback;

export function useSalesReceiptReviewPage({
  id,
  route,
  router,
  reviewCompletion,
  start,
  t,
  notify,
}) {
  const correctionsOpen = ref(false);
  const saving = ref(false);
  const savedSignatureDate = ref('');
  const signatureDate = ref('');
  const review = computed(() =>
    salesReceiptReviewContext(start.transaction.value)
  );
  const headerSubtitle = computed(() =>
    [start.transaction.value?.title, start.transaction.value?.address?.endereco]
      .filter(Boolean)
      .join(' · ')
  );
  const blockComplete = block =>
    block.key === 'signature_date'
      ? Boolean(signatureDate.value)
      : Boolean(start.receipt.document.value);
  const canComplete = computed(
    () =>
      Boolean(review.value.blocks.length) &&
      start.canEdit.value &&
      !saving.value &&
      review.value.blocks.every(blockComplete)
  );

  watch(
    () => start.transaction.value?.profile?.data_assinatura,
    value => {
      signatureDate.value = value || '';
      savedSignatureDate.value = value || '';
    },
    { immediate: true }
  );

  const goBack = () =>
    router.push(
      salesReviewLocation({
        accountId: route.params.accountId,
        salesTransactionId: id.value,
      })
    );
  const saveReviewChecks = checks => {
    if (!reviewCompletion?.save) return true;

    return reviewCompletion.save({ section: 'recibo', checks });
  };

  const saveSignatureDate = async ({ allowEmpty = false } = {}) => {
    if (!review.value.blocks.some(block => block.key === 'signature_date')) {
      return true;
    }
    if (!signatureDate.value) return allowEmpty;
    if (signatureDate.value === savedSignatureDate.value) return true;

    saving.value = true;
    try {
      const response = await SalesTransactionsAPI.saveDraft(id.value, {
        data_assinatura: signatureDate.value,
      });
      const saved = response?.data || {};
      start.transaction.value = {
        ...start.transaction.value,
        ...saved,
        profile: {
          ...start.transaction.value?.profile,
          ...saved.profile,
          data_assinatura: signatureDate.value,
        },
      };
      savedSignatureDate.value = signatureDate.value;
      const checksSaved = await saveReviewChecks(
        review.value.sectionChecks.signature_date
      );
      if (!checksSaved) return false;
      notify(t('SALES.DELIVERY.REVIEW_RECEIPT.ALERTS.DATE_SAVED'));
      return true;
    } catch (error) {
      notify(
        errorMessage(error, t('SALES.DELIVERY.REVIEW_RECEIPT.ERRORS.SAVE_DATE'))
      );
      return false;
    } finally {
      saving.value = false;
    }
  };

  const saveAndExit = async () => {
    if (await saveSignatureDate({ allowEmpty: true })) goBack();
  };

  const complete = async () => {
    if (!canComplete.value) {
      notify(t('SALES.DELIVERY.REVIEW_RECEIPT.ERRORS.INCOMPLETE'));
      return false;
    }
    if (await saveSignatureDate()) {
      goBack();
      return true;
    }
    return false;
  };
  const uploadReceipt = async file => {
    const uploaded = await start.receipt.upload(file);
    if (!uploaded) return null;

    return (await saveReviewChecks(review.value.sectionChecks.signed_receipt))
      ? uploaded
      : null;
  };
  const beforeSelectBlock = ({ currentKey, targetKey }) =>
    saveReviewNavigationBeforeSwitch({
      completeCurrent: () => true,
      currentSection: currentKey,
      reviewMode: true,
      saveCurrent: () =>
        currentKey === 'signature_date' ? saveSignatureDate() : true,
      switchTo: () => {},
      targetSection: targetKey,
    });

  return {
    beforeSelectBlock,
    canComplete,
    closeCorrections: () => {
      correctionsOpen.value = false;
    },
    complete,
    correctionsOpen,
    goBack,
    headerSubtitle,
    openCorrections: () => {
      correctionsOpen.value = true;
    },
    review,
    saveAndExit,
    saveDate: saveSignatureDate,
    saving,
    signatureDate,
    uploadReceipt,
  };
}
